import { EnvelopeSimple, LockKey } from '@phosphor-icons/react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { BusyLabel } from '@/components/busy-label'
import { useUiStore } from '@/lib/ui/ui-store'

import { authApi } from '../api'
import { AuthLayout } from '../components/auth-layout'
import { GoogleButton } from '../components/google-button'
import { Notice, type NoticeTone } from '../components/notice'
import { PasswordField } from '../components/password-field'
import { FormField } from '../components/form-field'
import { loginErrorKey } from '../error-keys'
import {
  loginSchema,
  forgotPasswordSchema,
  zodResolver,
  type LoginValues,
} from '../schemas'
import { completeLogin, postAuthDestination } from '../session'

/**
 * Login (UI_PARITY §3.2). The Google provider is disabled with a clear message
 * when `VITE_GOOGLE_OAUTH_ENABLED=false` (D-044).
 */
export function LoginScreen() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const setupProfile = searchParams.get('setupProfile') === '1'

  const [busy, setBusy] = useState(false)
  const [apiErrorKey, setApiErrorKey] = useState<string | null>(null)
  const [forgotBusy, setForgotBusy] = useState(false)
  const [forgotNotice, setForgotNotice] = useState<{
    key: string
    tone: NoticeTone
  } | null>(null)

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
    mode: 'onSubmit',
  })

  const validationKey =
    errors.email?.message ?? errors.password?.message ?? null
  const validationFailed = Boolean(validationKey)

  async function submit(values: LoginValues) {
    setBusy(true)
    setApiErrorKey(null)
    setForgotNotice(null)
    try {
      const tokens = await authApi.login(values)
      await completeLogin(tokens)
      const destination = postAuthDestination({
        setupProfile,
        from: (location.state as { from?: string } | null)?.from,
        pendingActivityPath: useUiStore.getState().pendingActivityPath,
      })
      navigate(destination, { replace: true })
    } catch (error) {
      setApiErrorKey(loginErrorKey(error))
    } finally {
      setBusy(false)
    }
  }

  function loginWithGoogle(idToken: string) {
    setBusy(true)
    setApiErrorKey(null)
    void authApi
      .googleLogin(idToken)
      .then(async (response) => {
        if ('registrationRequired' in response) {
          navigate('/register-google', {
            replace: true,
            state: {
              idToken,
              email: response.email ?? '',
              setupProfile,
            },
          })
          return
        }
        await completeLogin(response)
        navigate(
          postAuthDestination({
            setupProfile,
            from: (location.state as { from?: string } | null)?.from,
            pendingActivityPath: useUiStore.getState().pendingActivityPath,
          }),
          { replace: true },
        )
      })
      .catch((error: unknown) => setApiErrorKey(loginErrorKey(error)))
      .finally(() => setBusy(false))
  }

  async function requestPasswordReset() {
    const parsed = forgotPasswordSchema.safeParse({ email: getValues('email') })
    if (!parsed.success) {
      setForgotNotice({ key: 'auth.login.forgotValidation', tone: 'error' })
      return
    }

    setForgotBusy(true)
    setForgotNotice(null)
    try {
      const response = await authApi.forgotPassword(parsed.data.email)
      setForgotNotice({
        key: response.emailDeliveryAvailable
          ? 'auth.login.forgotSent'
          : 'auth.login.forgotUnavailable',
        tone: 'info',
      })
    } catch (error) {
      setForgotNotice({ key: loginErrorKey(error), tone: 'error' })
    } finally {
      setForgotBusy(false)
    }
  }

  const shownErrorKey = apiErrorKey ?? (validationFailed ? validationKey : null)

  return (
    <AuthLayout
      title={t('screens.login.title')}
      titleVisible={false}
      subtitle={t('screens.login.subtitle')}
      maxWidth={520}
      backTo="/onboarding"
    >
      <form
        className="space-y-3"
        noValidate
        onSubmit={handleSubmit((values) => void submit(values))}
      >
        <FormField
          label={t('auth.login.emailLabel')}
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          spellCheck={false}
          startIcon={<EnvelopeSimple size={20} weight="bold" />}
          aria-invalid={Boolean(shownErrorKey) || undefined}
          {...register('email')}
        />
        <PasswordField
          label={t('auth.login.passwordLabel')}
          autoComplete="current-password"
          hideToggle
          startIcon={<LockKey size={20} weight="bold" />}
          aria-invalid={Boolean(shownErrorKey) || undefined}
          {...register('password')}
        />

        {shownErrorKey ? (
          <Notice tone="error">{t(shownErrorKey)}</Notice>
        ) : null}
        {forgotNotice ? (
          <Notice tone={forgotNotice.tone}>{t(forgotNotice.key)}</Notice>
        ) : null}

        <button
          type="submit"
          className="btn-primary w-full"
          disabled={busy}
          aria-busy={busy}
        >
          <BusyLabel
            busy={busy}
            label={t('auth.login.submit')}
            busyLabel={t('auth.login.submitting')}
          />
        </button>

        <div className="pt-1">
          <GoogleButton disabled={busy} onCredential={loginWithGoogle} />
        </div>

        <div className="flex items-center gap-3 py-1" aria-hidden="true">
          <span className="h-px flex-1 bg-line" />
          <span className="text-sm text-muted">{t('auth.common.or')}</span>
          <span className="h-px flex-1 bg-line" />
        </div>

        <Link
          to="/register"
          className="btn-secondary w-full bg-white"
          aria-disabled={busy}
        >
          {t('auth.login.register')}
        </Link>

        <button
          type="button"
          className="btn-ghost w-full"
          onClick={() => void requestPasswordReset()}
          disabled={busy || forgotBusy}
        >
          {forgotBusy
            ? t('auth.login.forgotSubmitting')
            : t('auth.login.forgot')}
        </button>
      </form>
    </AuthLayout>
  )
}
